import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import './feed-data-form.scss'
import { AppService } from '@halo/services/app.service'
import { Breadcrumb, Form, Input, Radio, Select, Switch, Table, Tooltip } from 'antd'
import { DomainService } from '@halo/services/domain.service'
import { Button, NotificationSummaryCard, PageHeader } from '@pushly/aqe/lib/components'
import { useForm } from 'antd/es/form/Form'
import { RelativeDate, relativeDateOf } from '@pushly/models/lib/structs/relative-date'
import { classnames } from '@halo/_utils/classnames'
import {
    ComboSegmentSelection,
    DayPartSelect,
    ReachWell,
    TemplateKeywordsField,
    Well,
} from '@pushly/aqe/lib/components/index'
import { FeedType } from '@pushly/models/lib/enums/feed-type'
import { RelativeDateDisplayMetric } from '@pushly/models/lib/enums/relative-date-display-metric'
import { SegmentDto } from '@halo/dtos/segment.dto'
import {
    daysToSeconds,
    hoursToSeconds,
    minutesToSeconds,
    monthsToSeconds,
    secondsToDays,
    secondsToHours,
    secondsToMinutes,
    secondsToMonths,
} from '@pushly/aqe/lib/utils/index'
import { FeedService } from '@halo/services/feed.service'
import { numberWithCommas } from '@halo/_utils/numeral'
import { InfoCircleOutlined, Loading3QuartersOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { useMountedRef } from '@halo/hooks/use-mounted-ref'
import { useDebouncer } from '@halo/hooks/use-debouncer'
import { DomainDto } from '@halo/dtos/domain.dto'
import { extractSummaryNotification } from '@halo/_utils/feed-notifications'
import { RouteComponentProps } from 'react-router-dom'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { useService } from '@pushly/aqe/lib/hooks'
import { DOMAINS_ROUTE } from '@halo/portals/post-auth/domains'
import { simpleNotification } from '@halo/_utils/notifications'
import { PreviewState } from '@pushly/cuttlefish'
import { FdfFormMode } from '@halo/enums/fdf-form-mode.enum'
import deepEqual from 'react-fast-compare'
import { SegmentService } from '@halo/services/index'
import { FeedDto } from '@halo/dtos/feed.dto'
import { getEnabledDeliveryChannels } from '@halo/_utils/domain'
import { DeliveryChannelSelector } from '@pushly/aqe/lib/components/delivery-channel-selector/delivery-channel-selector'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { HolidayFilter } from '@pushly/models/lib/enums/holiday-filter-type'
import { holidaySkipCountryOptions } from '@halo/enums/holiday-skip-country-options'
import { Feed, MutableFeed } from '@pushly/models/lib/structs/feeds/feed'
import { FeedSendStrategy } from '@pushly/aqe/lib/enums'
import { FixedFeedSchedule, MutableFixedFeedSchedule } from '@pushly/models/lib/structs/feeds/fixed-feed-schedule'
import { AsapFeedSchedule, MutableAsapFeedSchedule } from '@pushly/models/lib/structs/feeds/asap-feed-schedule'
import { MutableFeedConfiguration } from '@pushly/models/lib/structs/feeds/feed-configuration'
import { MutableHolidaySkipConfiguration } from '@pushly/models/lib/structs/feeds/holiday-skip-configuration'
import { hydrateFeedModel } from '@halo/_utils/models-hydration'
import { MutableStzFeedSchedule } from '@pushly/models/lib/structs/feeds/stz-feed-schedule'
import { enumFromValue } from '@pushly/models'
import {
    calculateRelativeDate,
    getDefaultNotificationLifespanInputValue,
    getDefaultNotificationTtlMetric,
} from '@halo/portals/post-auth/domains/feeds/utils'
import { MutableLimitedScheduleDatesConfiguration } from '@pushly/models/lib/structs/feeds/limited-schedule-dates-configuration'
import { FeedScheduledDatesEditor } from '@halo/portals/post-auth/domains/feeds/feed-scheduled-dates-editor'
import { FeedArticleAgeEditor } from '@halo/portals/post-auth/domains/feeds/feed-article-age-editor'
import { MutableFeedPath } from '@pushly/models/lib/structs/feeds/feed-path'
import { MutableFeedPathProperties } from '@pushly/models/lib/structs/feeds/feed-path-properties'
import { FeedPathPropertyExpressionsWell } from '@halo/portals/post-auth/domains/feeds/feed-path-property-expressions-well'
import { BetterTimePicker } from '@pushly/aqe/lib/components/better-timepicker/better-timepicker'
import moment, { Moment } from 'moment-timezone'

interface IFeedDataFormProps
    extends RouteComponentProps<
        any,
        any,
        {
            domain?: DomainDto
            feed: FeedDto
            mode?: FdfFormMode
        }
    > {}

const dayIsoMap: { [index: string]: number } = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7,
}

function generateNewFeedModel(domainId: number): Feed {
    const newFeed = new MutableFeed()

    newFeed.enabled = false
    newFeed.domainId = domainId
    newFeed.feedType = FeedType.RSS

    newFeed.config = new MutableFeedConfiguration()
    newFeed.config.fieldOptions = {
        title: {
            enabled: true,
            use_page_analysis: true,
            character_limit: 100,
            placeholder: '...',
        },
        body: {
            enabled: false,
            use_page_analysis: false,
        },
        image: {
            enabled: true,
            use_page_analysis: true,
        },
        keywords: {
            enabled: true,
            use_page_analysis: true,
        },
    }
    newFeed.config.articleRecycling = false
    newFeed.config.articleMaxAge = relativeDateOf({
        displayMetric: RelativeDateDisplayMetric.DAYS,
        intervalSeconds: 604800,
    })
    newFeed.isAll = true

    newFeed.config.schedule = new MutableFixedFeedSchedule()
    newFeed.config.schedule.sendStrategy = FeedSendStrategy.FIXED
    newFeed.config.schedule.dayOfWeekIso = [1, 2, 3, 4, 5, 6, 7]
    newFeed.config.schedule.holidaySkip = new MutableHolidaySkipConfiguration()
    newFeed.config.schedule.holidaySkip.enabled = false
    newFeed.config.schedule.limitedScheduleDates = new MutableLimitedScheduleDatesConfiguration()
    newFeed.config.schedule.limitedScheduleDates.startDate = moment().format('YYYY-MM-DD')

    return newFeed
}

// ---------- Interval Seconds Input Seconds Calculation ----------
export const calculateIntervalSecondsInputValue = (value: number, metric: RelativeDateDisplayMetric) => {
    let intervalSeconds

    switch (metric) {
        case RelativeDateDisplayMetric.MINUTES:
            intervalSeconds = minutesToSeconds(value)
            break
        case RelativeDateDisplayMetric.HOURS:
            intervalSeconds = hoursToSeconds(value)
            break
        case RelativeDateDisplayMetric.DAYS:
            intervalSeconds = daysToSeconds(value)
            break
        case RelativeDateDisplayMetric.MONTHS:
            intervalSeconds = monthsToSeconds(value)
            break
    }

    return intervalSeconds
}

export function FeedDataForm(props: IFeedDataFormProps) {
    const appService = useService(AppService)
    const domainService = useService(DomainService)
    const segmentService = useService(SegmentService)
    const feedService = useService(FeedService)
    const [debounce] = useDebouncer()

    const mode = props.location.state?.mode ?? (/edit$/.test(props.match.url) ? FdfFormMode.EDIT : FdfFormMode.CREATE)
    const domainId = props.location.state?.domain?.id ?? parseInt(props.match.params.domainId, 10)
    const feedId = props.location.state?.feed?.id ?? parseInt(props.match.params.feedId, 10)

    const [loadingFeed, setLoadingFeed] = useState<boolean>(false)
    const [originalFeed, setOriginalFeed] = useState<Feed | undefined>(() => {
        return props.location.state?.feed ? hydrateFeedModel(props.location.state.feed) : undefined
    })
    const [workingModel, setWorkingModel] = useState<Feed>(() => {
        return props.location.state?.feed ? hydrateFeedModel(props.location.state.feed) : undefined
    })
    const [loadingDomain, setLoadingDomain] = useState<boolean>(false)
    const [domain, setDomain] = useState<DomainDto>(props.location.state?.domain)
    const [domainEnabledChannels, setDomainEnabledChannels] = useState<DeliveryChannel[]>([])

    const loadingPrimaryResources = !workingModel || loadingFeed || loadingDomain

    const [loadingSegments, setLoadingSegments] = useState<boolean>(false)
    const [domainSegments, setDomainSegments] = useState<SegmentDto[]>()
    const audienceChanged = useRef<boolean>(false)

    const [_mounted, runIfMounted] = useMountedRef()

    const [loadingReach, setLoadingReach] = React.useState(false)
    const [currReach, setCurrReach] = React.useState<number | undefined>()

    const [previewIndex, setPreviewIndex] = useState(0)
    const [isInitialPreviewLoad, setIsInitialPreviewLoad] = useState(true)
    const [loadingPreview, setLoadingPreview] = React.useState(false)
    const [previewNotifications, setPreviewNotifications] = React.useState([])
    const [estimatedPreviewDeliveries, setEstimatedPreviewDeliveries] = React.useState([])
    const hasReachEstimate = currReach !== undefined

    // ---------- Pre-Schedule Input Value Management ----------
    const calculatePreScheduleInputValue = (config: RelativeDate) => {
        if (!config) return undefined

        const defaultSeconds = 3600

        return config?.displayMetric === RelativeDateDisplayMetric.HOURS
            ? secondsToHours(config?.intervalSeconds ?? defaultSeconds)
            : secondsToMinutes(config?.intervalSeconds ?? defaultSeconds)
    }

    const [preScheduleInputValue, setPreScheduleInputValue] = useState(() => {
        return calculatePreScheduleInputValue((workingModel?.config.schedule as FixedFeedSchedule)?.deliveryDelay)
    })
    // ----------

    // ---------- Notification Lifespan Input Value Management ----------
    const [isDomainDefaultNotificationLifespan, setIsDefaultNotificationLifespan] = useState<boolean>(
        !workingModel?.config.notificationLifespan,
    )

    const [notificationLifespanInputValue, setNotificationLifespanInputValue] = useState(() =>
        getDefaultNotificationLifespanInputValue(workingModel, domain),
    )

    // ----------

    // ---------- Article Age Input Value Management ----------
    const calculateArticleAgeInputValue = (config: RelativeDate) => {
        const defaultSeconds = 604800

        return config?.displayMetric === RelativeDateDisplayMetric.MONTHS
            ? secondsToMonths(config?.intervalSeconds ?? defaultSeconds)
            : config?.displayMetric === RelativeDateDisplayMetric.DAYS
            ? secondsToDays(config?.intervalSeconds ?? defaultSeconds)
            : secondsToHours(config?.intervalSeconds ?? defaultSeconds)
    }

    const [articleAgeInputValue, setArticleAgeInputValue] = useState(() => {
        return calculateArticleAgeInputValue(workingModel?.config.articleMaxAge)
    })
    // ----------

    // ---------- Schedule Time Change Handlers ----------
    const handleStartTimeChange = (v: Moment | null) => {
        const updated = workingModel.toMutable()
        updated.config.schedule.startTime = v?.format('HH:mm')

        setWorkingModel(updated)
    }

    const handleEndTimeChange = (v: Moment | null) => {
        const updated = workingModel.toMutable()
        ;(updated.config.schedule as MutableAsapFeedSchedule).endTime = v?.format('HH:mm')

        setWorkingModel(updated)
    }
    // ----------

    /**
     * Fix for non-nullable excluded segment ids value.
     * An initial empty array value will be translated to null.
     * Subsequent updates to the component will populate the value
     * as expected.
     */
    const [excludedSegmentIds, setExcludedSegmentIds] = useState<number[]>(
        workingModel?.excludedSegmentIds?.length ? workingModel.excludedSegmentIds : null,
    )

    useEffect(() => {
        /**
         * If page was accessed directly with no feed state passed during
         * navigation we will load feed from remote end.
         */
        const loadFeedModel = async () => {
            if (!loadingFeed && !workingModel) {
                setLoadingFeed(true)

                let feed: Feed
                if (!feedId) {
                    feed = generateNewFeedModel(domainId)
                } else {
                    const { ok, data } = await feedService.fetchFeedById(domainId, feedId, {
                        showLoadingScreen: true,
                    })

                    if (ok) {
                        feed = hydrateFeedModel(data)
                    }
                }

                if (feed) {
                    setWorkingModel(feed)
                    setOriginalFeed(feed)

                    // reset article age input value from remote loaded state
                    const preSchedule = calculatePreScheduleInputValue(
                        (feed.config.schedule as FixedFeedSchedule).deliveryDelay,
                    )
                    setPreScheduleInputValue(preSchedule)

                    // reset article age input value from remote loaded state
                    const articleAge = calculateArticleAgeInputValue(feed.config.articleMaxAge)
                    setArticleAgeInputValue(articleAge)

                    const defaultSegment = domainSegments?.find((ds) => ds.is_default)
                    if (defaultSegment) {
                        checkAndSetIfFeedTargetsAll(feed, defaultSegment)
                    }

                    if (!excludedSegmentIds) {
                        setExcludedSegmentIds(feed.excludedSegmentIds)
                    }
                }

                setLoadingFeed(false)
            }
        }

        loadFeedModel()
        if (!domain) {
            fetchDomain()
        }
        loadDomainSegments()
    }, [domain?.id, !domainSegments])

    const [form] = useForm()

    useEffect(() => {
        const fetchReach = async () => {
            if (workingModel) {
                setLoadingReach(true)

                const res = await segmentService.estimateSegmentReach(
                    domainId,
                    workingModel.segmentIds ?? [],
                    workingModel.excludedSegmentIds ?? [],
                    workingModel.config.channels,
                    {
                        cancellationKey: 'feed-fetch-reach',
                    },
                )

                if (!res.cancelled) {
                    runIfMounted(() => {
                        setCurrReach(res.data)
                        setLoadingReach(false)
                    })
                }
            }
        }

        fetchReach()
    }, [
        domainId,
        JSON.stringify(workingModel?.segmentIds),
        JSON.stringify(workingModel?.excludedSegmentIds),
        JSON.stringify(workingModel?.config.channels),
    ])

    useEffect(() => {
        const fetchNotificationPreview = async () => {
            if (workingModel) {
                if (isInitialPreviewLoad && !workingModel.url) {
                    setIsInitialPreviewLoad(false)

                    return
                }

                if (!isValidPreviewRequest()) {
                    if (workingModel.url && previewNotifications.length === 0) {
                        setPreviewNotifications([])
                    }
                    setEstimatedPreviewDeliveries([])
                }

                if (isValidPreviewRequest()) {
                    if (previewNotifications.length === 0 || !loadingPreview) {
                        setPreviewIndex(0)
                        setLoadingPreview(true)
                    }

                    const res =
                        workingModel.url &&
                        (await feedService.fetchFeedPreview(
                            domainId,
                            workingModel.config,
                            workingModel.url,
                            workingModel.feedType,
                            5,
                            { cancellationKey: 'feeds-v4-preview-fetch' },
                        ))

                    if (!res.ok) {
                        setPreviewNotifications([])
                        setEstimatedPreviewDeliveries([])
                        setLoadingPreview(false)
                        return
                    }

                    res.data?.notifications && setPreviewNotifications([...res.data.notifications])
                    res.data?.estimated_delivery_times &&
                        setEstimatedPreviewDeliveries([...res.data.estimated_delivery_times.slice(0, 7)])
                    setLoadingPreview(false)
                }
            }
        }

        fetchNotificationPreview()
    }, [
        domainId,
        JSON.stringify(workingModel?.config),
        JSON.stringify(workingModel?.url),
        JSON.stringify(workingModel?.feedType),
    ])

    useEffect(() => {
        // reset notification lifespan input value from remote loaded state or domain default
        if (workingModel?.config.notificationLifespan) {
            setIsDefaultNotificationLifespan(false)
            const notificationLifespan = calculateRelativeDate(
                workingModel.config.notificationLifespan.intervalSeconds,
                workingModel.config.notificationLifespan.displayMetric,
            )
            setNotificationLifespanInputValue(notificationLifespan)
        } else if (domain?.default_notification_ttl_seconds) {
            setIsDefaultNotificationLifespan(true)
            const domainDefaultNotificationLifespan = calculateRelativeDate(
                domain.default_notification_ttl_seconds,
                enumFromValue(RelativeDateDisplayMetric, domain.display_meta.default_notification_ttl_metric),
            )
            setNotificationLifespanInputValue(domainDefaultNotificationLifespan)
        }

        if (!domainEnabledChannels.length && domain) {
            const activeDomainChannels = getEnabledDeliveryChannels(domain, true)
            setDomainEnabledChannels(activeDomainChannels)
        }
    }, [loadingPrimaryResources, workingModel, domain])

    useEffect(() => {
        if (!feedId && workingModel && domainEnabledChannels) {
            const updated = workingModel.toMutable()
            updated.config.channels = domainEnabledChannels

            setWorkingModel(updated)
        }
    }, [domainEnabledChannels, feedId])

    const checkAndSetIfFeedTargetsAll = React.useCallback((model: Feed, defaultSegment: SegmentDto) => {
        if (!audienceChanged.current) {
            if (model.isAll && defaultSegment) {
                const updated = model.toMutable()
                updated.segmentIds = [defaultSegment.id]
                setWorkingModel(updated)
            } else if (model.segmentIds?.[0]?.toString() === defaultSegment.id.toString()) {
                const updated = model.toMutable()
                updated.isAll = true
                setWorkingModel(updated)
            }
        }
    }, [])

    async function fetchDomain() {
        if (!domain) {
            setLoadingDomain(true)

            const { ok, data } = await domainService.fetchById(domainId)

            if (ok) {
                setDomain(data)
                setNotificationLifespanInputValue(getDefaultNotificationLifespanInputValue(workingModel, data))
            }

            setLoadingDomain(false)
        }
    }

    async function loadDomainSegments() {
        setLoadingSegments(true)

        const { ok, data } = await segmentService.fetchSegmentsByDomainId(domainId, {
            query: {
                includeTreated: 0,
                pagination: 0,
                fields: 'id,name,source,computedStatus,isDefault,iconUrl',
                source: 'standard',
            },
        })

        if (ok) {
            setDomainSegments(data)

            const defaultSegment = data?.find((ds) => ds.is_default)
            if (workingModel && defaultSegment) {
                checkAndSetIfFeedTargetsAll(workingModel, defaultSegment)
            }
        }

        setLoadingSegments(false)
    }

    async function save() {
        if (isValidFeed()) {
            const feedDto = workingModel.serialize()
            const { ok, data: feed } =
                mode !== FdfFormMode.EDIT
                    ? await feedService.create(domainId, feedDto, {
                          showLoadingScreen: true,
                      })
                    : await feedService.patchFeed(domainId, workingModel.id, getCurrentValues(), {
                          showLoadingScreen: true,
                      })

            if (ok) {
                appService.route(`/domains/${domainId}/feeds`)
            }
        }
    }

    function getCurrentValues(): Feed {
        const values = new MutableFeed()

        if (workingModel.enabled !== originalFeed.enabled) {
            values.enabled = workingModel.enabled
        }

        if (workingModel.name) {
            if (!deepEqual(workingModel.name.trim(), originalFeed.name)) {
                values.name = workingModel.name.trim()
            }
        }

        if (workingModel.feedType !== originalFeed.feedType) {
            values.feedType = workingModel.feedType
        }

        if (workingModel.url) {
            if (!deepEqual(workingModel.url.trim(), originalFeed.url)) {
                values.url = workingModel.url.trim()
            }
        }

        if (workingModel.segmentIds) {
            if (!deepEqual(workingModel.segmentIds, originalFeed.segmentIds)) {
                values.segmentIds = workingModel.segmentIds
            }
        }

        if (workingModel.excludedSegmentIds || originalFeed.excludedSegmentIds) {
            if (!deepEqual(workingModel.excludedSegmentIds, originalFeed.excludedSegmentIds)) {
                values.excludedSegmentIds = workingModel.excludedSegmentIds ? workingModel.excludedSegmentIds : []
            }
        }

        // config
        if (workingModel.config) {
            const workingConfig = workingModel.config
            const originalConfig = originalFeed.config

            if (!deepEqual(workingConfig, originalConfig)) {
                values.config = new MutableFeedConfiguration()

                // schedule
                // any changes to schedule will send up the entire schedule object
                if (!deepEqual(workingConfig.schedule, originalConfig.schedule)) {
                    values.config.schedule = workingConfig.schedule
                }

                // field options
                // any changes to field options will send up the entire field options object
                if (!deepEqual(workingConfig.fieldOptions, originalConfig.fieldOptions)) {
                    values.config.fieldOptions = workingConfig.fieldOptions
                }

                // article constraints
                if (workingConfig.requiredKeywords) {
                    if (
                        !deepEqual(workingConfig.requiredKeywords.sort(), originalConfig.requiredKeywords?.sort() || [])
                    ) {
                        values.config.requiredKeywords = workingConfig.requiredKeywords
                    }
                }

                if (workingConfig.restrictedKeywords) {
                    if (
                        !deepEqual(
                            workingConfig.restrictedKeywords.sort(),
                            originalConfig.restrictedKeywords?.sort() || [],
                        )
                    ) {
                        values.config.restrictedKeywords = workingConfig.restrictedKeywords
                    }
                }

                if (workingConfig.urlAllowlist) {
                    if (!deepEqual(workingConfig.urlAllowlist.sort(), originalConfig.urlAllowlist?.sort() || [])) {
                        values.config.urlAllowlist = workingConfig.urlAllowlist
                    }
                }

                if (workingConfig.urlDenylist) {
                    if (!deepEqual(workingConfig.urlDenylist.sort(), originalConfig.urlDenylist?.sort() || [])) {
                        values.config.urlDenylist = workingConfig.urlDenylist
                    }
                }

                if (workingConfig.titleDenylist) {
                    if (!deepEqual(workingConfig.titleDenylist.sort(), originalConfig.titleDenylist?.sort() || [])) {
                        values.config.titleDenylist = workingConfig.titleDenylist
                    }
                }

                if (workingModel.config.articleRecycling !== originalFeed.config.articleRecycling) {
                    values.config.articleRecycling = workingModel.config.articleRecycling
                }

                if (!deepEqual(workingConfig.articleMaxAge, originalConfig.articleMaxAge)) {
                    values.config.articleMaxAge = workingConfig.articleMaxAge
                }

                if (!deepEqual(workingConfig.notificationLifespan, originalConfig.notificationLifespan)) {
                    values.config.notificationLifespan = workingConfig.notificationLifespan
                }

                if (!deepEqual(workingConfig.channels, originalConfig.channels)) {
                    values.config.channels = workingConfig.channels
                }

                // JSONPath Expressions
                // any changes to feedJsonpath will send up the entire feedJsonpath object
                if (!deepEqual(workingConfig.feedJsonpath, originalConfig.feedJsonpath)) {
                    values.config.feedJsonpath = workingConfig.feedJsonpath
                }

                // Xpath Expressions
                // any changes to feedXpath will send up the entire feedXpath object
                if (!deepEqual(workingConfig.feedXpath, originalConfig.feedXpath)) {
                    values.config.feedXpath = workingConfig.feedXpath
                }
            }
        }

        return values
    }

    const handleBreadcrumbClick = React.useCallback((ev: React.MouseEvent<HTMLAnchorElement>) => {
        if (!ev.metaKey) {
            ev.preventDefault()
            appService.route(ev.currentTarget.getAttribute('href'))
        }
    }, [])

    const isValidFeed = React.useCallback((): boolean => {
        let validFeed = true
        if (!workingModel.name) {
            validFeed = false
            simpleNotification('error', 'Name is required')
        }

        if (!workingModel.url) {
            validFeed = false
            simpleNotification('error', 'URL is required')
        }

        if (!workingModel.config.schedule.startTime) {
            validFeed = false
            simpleNotification('error', 'Start Time is required')
        }

        if (workingModel.config.schedule.sendStrategy === FeedSendStrategy.ASAP) {
            if (!workingModel.config.schedule.endTime) {
                validFeed = false
                simpleNotification('error', 'End Time is required')
            }

            if (!workingModel.config.schedule.minFrequency?.intervalSeconds) {
                validFeed = false
                simpleNotification('error', 'Send Frequency is required')
            }
        }

        if (workingModel.feedType === FeedType.JSON) {
            if (!workingModel.config.feedJsonpath.articles) {
                validFeed = false
                simpleNotification('error', 'JSONPath Articles is required')
            }

            if (!workingModel.config.feedJsonpath.properties.url) {
                validFeed = false
                simpleNotification('error', 'JSONPath URL is required')
            }

            if (!workingModel.config.feedJsonpath.properties.title) {
                validFeed = false
                simpleNotification('error', 'JSONPath Title is required')
            }
        }

        if (workingModel.feedType === FeedType.XML) {
            if (!workingModel.config.feedXpath.articles) {
                validFeed = false
                simpleNotification('error', 'XPath Articles is required')
            }

            if (!workingModel.config.feedXpath.properties.url) {
                validFeed = false
                simpleNotification('error', 'XPath URL is required')
            }

            if (!workingModel.config.feedXpath.properties.title) {
                validFeed = false
                simpleNotification('error', 'XPath Title is required')
            }
        }

        return validFeed
    }, [
        workingModel?.name,
        workingModel?.url,
        JSON.stringify(workingModel?.config?.schedule),
        JSON.stringify(workingModel?.config?.feedJsonpath),
        JSON.stringify(workingModel?.config?.feedXpath),
    ])

    const isValidPreviewRequest = React.useCallback((): boolean => {
        let validFeedPreview = true

        if (!workingModel?.url || !workingModel?.config?.schedule?.startTime) {
            validFeedPreview = false
        }

        return validFeedPreview
    }, [workingModel?.url, workingModel?.config?.schedule?.startTime])

    const extractOption = React.useCallback((segment: SegmentDto) => {
        return {
            value: segment.id,
            label: segment.name,
            title: segment.name,
        }
    }, [])

    return loadingPrimaryResources ? null : (
        <div>
            <PageHeader
                title={mode !== FdfFormMode.EDIT ? 'Create Feed' : 'Edit Feed'}
                onTitleSet={noop}
                append={
                    !domain ? (
                        <Loading3QuartersOutlined spin={loadingDomain} />
                    ) : (
                        <Breadcrumb>
                            <Breadcrumb.Item href={DOMAINS_ROUTE} onClick={handleBreadcrumbClick}>
                                Domains
                            </Breadcrumb.Item>
                            <Breadcrumb.Item href={`${DOMAINS_ROUTE}/${domain?.id}`} onClick={handleBreadcrumbClick}>
                                {domain?.display_name}
                            </Breadcrumb.Item>
                            <Breadcrumb.Item
                                href={`${DOMAINS_ROUTE}/${domain?.id}/feeds`}
                                onClick={handleBreadcrumbClick}
                            >
                                Feeds
                            </Breadcrumb.Item>
                            {workingModel.id ? (
                                <Breadcrumb.Item>ID: {workingModel.id}</Breadcrumb.Item>
                            ) : (
                                <Breadcrumb.Item>Create</Breadcrumb.Item>
                            )}
                        </Breadcrumb>
                    )
                }
            />
            <Form
                onFinish={save}
                form={form}
                initialValues={{
                    type: workingModel.feedType ?? FeedType.RSS,
                    'delivery-strategy': workingModel.config.schedule.sendStrategy,
                }}
            >
                <div className="feed-data-form-container">
                    <div className="feed-data-form-left-pane">
                        <Well title="Details" hideFooter={true} className="feed-enabled-well type-3">
                            <Form.Item label="Name">
                                <Input
                                    autoComplete="off"
                                    size="small"
                                    defaultValue={workingModel.name ?? undefined}
                                    onChange={(ev) => {
                                        const name = ev.target.value
                                        debounce(() => {
                                            const updated = workingModel.toMutable()
                                            updated.name = name
                                            setWorkingModel(updated)
                                        })
                                    }}
                                />
                            </Form.Item>

                            <Form.Item label="Type" name="type">
                                <Select<FeedType>
                                    size="small"
                                    onChange={(v) => {
                                        const updated = workingModel.toMutable()
                                        updated.feedType = v as FeedType

                                        if (v === FeedType.JSON) {
                                            updated.config.feedJsonpath = new MutableFeedPath()
                                            updated.config.feedJsonpath.properties = new MutableFeedPathProperties()

                                            delete updated.config.feedXpath
                                        } else if (v === FeedType.XML) {
                                            updated.config.feedXpath = new MutableFeedPath()
                                            updated.config.feedXpath.properties = new MutableFeedPathProperties()

                                            delete updated.config.feedJsonpath
                                        } else {
                                            delete updated.config.feedJsonpath
                                            delete updated.config.feedXpath
                                        }

                                        setWorkingModel(updated)
                                    }}
                                >
                                    return (
                                    <Select.Option key="rss" value={FeedType.RSS}>
                                        RSS
                                    </Select.Option>
                                    <Select.Option key="json" value={FeedType.JSON}>
                                        JSON
                                    </Select.Option>
                                    <Select.Option key="xml" value={FeedType.XML}>
                                        XML
                                    </Select.Option>
                                    );
                                </Select>
                            </Form.Item>

                            <Form.Item label="URL">
                                <Input
                                    autoComplete="off"
                                    size="small"
                                    defaultValue={workingModel.url ?? undefined}
                                    onChange={(ev) => {
                                        const url = ev.target.value
                                        debounce(() => {
                                            const updated = workingModel.toMutable()
                                            updated.url = url
                                            setWorkingModel(updated)
                                        })
                                    }}
                                />
                            </Form.Item>
                            <Form.Item label="Status">
                                <Switch
                                    className="feed-enabled-switch"
                                    size="small"
                                    checked={workingModel.enabled}
                                    onChange={(v) => {
                                        const updated = workingModel.toMutable()
                                        updated.enabled = v

                                        setWorkingModel(updated)
                                    }}
                                />
                            </Form.Item>
                        </Well>
                        {domainEnabledChannels.length > 1 && (
                            <DeliveryChannelSelector
                                className="type-3"
                                type="multiple"
                                loading={loadingDomain}
                                value={workingModel.config.channels}
                                onChange={(val) => {
                                    const updated = workingModel.toMutable()

                                    updated.config.channels = val

                                    setWorkingModel(updated)
                                }}
                                visibleChannels={domainEnabledChannels}
                            />
                        )}
                        <Well
                            title="Audience"
                            hideFooter={true}
                            loading={loadingPrimaryResources || loadingSegments}
                            className="audience-well type-3"
                        >
                            <Radio.Group
                                className="audience-builder-type-radio"
                                size="small"
                                buttonStyle="solid"
                                value={workingModel.isAll ? 'all' : 'segments'}
                                onChange={(ev) => {
                                    audienceChanged.current = true
                                    const v = ev.target.value

                                    let segmentIds: any[]
                                    let isAll = false
                                    const defaultSegment = domainSegments.filter((ds) => ds.is_default)
                                    if (v !== 'all') {
                                        segmentIds = []
                                    } else if (!!defaultSegment) {
                                        segmentIds = [defaultSegment[0].id]
                                        isAll = true
                                    }

                                    const updated = workingModel.toMutable()
                                    updated.isAll = isAll
                                    updated.segmentIds = segmentIds

                                    setWorkingModel(updated)
                                }}
                            >
                                <Radio.Button value="all" disabled={false}>
                                    All Subscribers
                                </Radio.Button>
                                <Radio.Button value="segments">Specific Segments</Radio.Button>
                            </Radio.Group>

                            {domainSegments && (
                                <ComboSegmentSelection
                                    size="small"
                                    switchSize="small"
                                    hideIncluded={workingModel.isAll}
                                    value={{
                                        allSubscribers: workingModel.isAll,
                                        segmentIds: workingModel.segmentIds,
                                        excludedSegmentIds,
                                    }}
                                    onChange={(v) => {
                                        const updated = workingModel.toMutable()
                                        updated.segmentIds = v.segmentIds
                                        updated.excludedSegmentIds = v.excludedSegmentIds
                                        setExcludedSegmentIds(v.excludedSegmentIds)

                                        setWorkingModel(updated)
                                    }}
                                    options={domainSegments.map(extractOption)}
                                />
                            )}
                        </Well>
                        <Well title="Schedule" hideFooter={true} className="send-schedule-well type-3">
                            <Form.Item
                                label={
                                    <div className="delivery-strategy-label">
                                        Delivery Strategy
                                        <Tooltip
                                            overlayClassName="wide-tooltip"
                                            title={
                                                <React.Fragment>
                                                    <p>
                                                        The delivery strategy determines when and how often a
                                                        notification will be created for a feed:
                                                    </p>

                                                    <ul className="tooltip-ul">
                                                        <li>
                                                            ASAP: A notification will be created every time a new
                                                            article is discovered and a previous notification has not
                                                            been sent based on the specified send frequency.
                                                        </li>

                                                        <li>
                                                            Fixed: A notification will be scheduled once a day at the
                                                            specified send time in the domain’s time zone. If a new
                                                            article is not found at the specified time we will continue
                                                            checking for a maximum of 1 hour after the send time.
                                                        </li>

                                                        <li>
                                                            Subscriber Time Zone: A notification will be scheduled at
                                                            the specified send time in each subscriber’s time zone. In
                                                            order to account for all time zones the article will be
                                                            chosen at least 24 hours in advance of the send time.
                                                        </li>
                                                    </ul>
                                                </React.Fragment>
                                            }
                                        >
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </div>
                                }
                                name="delivery-strategy"
                            >
                                <Radio.Group
                                    size="small"
                                    buttonStyle="solid"
                                    onChange={(ev) => {
                                        const updated = workingModel.toMutable()
                                        let schedule

                                        switch (ev.target.value as FeedSendStrategy) {
                                            case FeedSendStrategy.FIXED:
                                                schedule = new MutableFixedFeedSchedule()
                                                break
                                            case FeedSendStrategy.ASAP:
                                                schedule = new MutableAsapFeedSchedule()
                                                break
                                            case FeedSendStrategy.SUBSCRIBER_TIME_ZONE:
                                                schedule = new MutableStzFeedSchedule()
                                                break
                                        }
                                        schedule.sendStrategy = ev.target.value
                                        schedule.dayOfWeekIso = [1, 2, 3, 4, 5, 6, 7]

                                        schedule.limitedScheduleDates = new MutableLimitedScheduleDatesConfiguration()
                                        schedule.limitedScheduleDates.startDate = moment().format('YYYY-MM-DD')

                                        if (schedule.sendStrategy === FeedSendStrategy.ASAP) {
                                            const asapSchedule = new MutableAsapFeedSchedule()
                                            asapSchedule.startTime = '00:00:00'
                                            asapSchedule.endTime = '23:59:59'
                                            asapSchedule.minFrequency = relativeDateOf({
                                                intervalSeconds: 3600,
                                                displayMetric: RelativeDateDisplayMetric.MINUTES,
                                            })
                                        }

                                        updated.config.schedule = schedule
                                        setWorkingModel(updated)
                                        setEstimatedPreviewDeliveries([])
                                    }}
                                >
                                    <Radio.Button value={FeedSendStrategy.ASAP}>
                                        <span>ASAP</span>
                                    </Radio.Button>
                                    <Radio.Button value={FeedSendStrategy.FIXED}>
                                        <span>Fixed</span>
                                    </Radio.Button>
                                    <Radio.Button value={FeedSendStrategy.SUBSCRIBER_TIME_ZONE}>
                                        Subscriber Time Zone
                                    </Radio.Button>
                                </Radio.Group>
                            </Form.Item>
                            <FeedScheduledDatesEditor
                                scheduledDates={workingModel.config.schedule.limitedScheduleDates}
                                onChange={(next) => {
                                    const update = workingModel.toMutable()
                                    update.config.schedule.limitedScheduleDates = next
                                    setWorkingModel(update)
                                }}
                            />
                            <Form.Item
                                label="Send Time"
                                required={
                                    workingModel.config.schedule.sendStrategy === FeedSendStrategy.FIXED ||
                                    workingModel.config.schedule.sendStrategy === FeedSendStrategy.SUBSCRIBER_TIME_ZONE
                                }
                            >
                                <Form.Item>
                                    <Input.Group>
                                        <BetterTimePicker
                                            className="schedule-time-picker start-time"
                                            size="small"
                                            placeholder="Start time"
                                            use12Hours={true}
                                            format="h:mm A"
                                            value={
                                                workingModel.config.schedule.startTime
                                                    ? moment(workingModel.config.schedule.startTime, 'H:mm')
                                                    : undefined
                                            }
                                            onTimeChange={handleStartTimeChange}
                                            showNow={false}
                                        />
                                        {workingModel.config.schedule.sendStrategy !== FeedSendStrategy.ASAP && (
                                            <div className="ant-input-group-addon timezone-addon">
                                                {workingModel.config.schedule.sendStrategy === FeedSendStrategy.FIXED
                                                    ? domain?.timezone
                                                    : 'Subscriber Time Zone'}
                                            </div>
                                        )}
                                    </Input.Group>
                                </Form.Item>
                                {workingModel.config.schedule.sendStrategy === FeedSendStrategy.ASAP && (
                                    <Form.Item>
                                        <Input.Group>
                                            <BetterTimePicker
                                                className="schedule-time-picker end-time"
                                                size="small"
                                                placeholder="End time"
                                                use12Hours={true}
                                                format="h:mm A"
                                                value={
                                                    (workingModel.config.schedule as AsapFeedSchedule).endTime
                                                        ? moment(
                                                              (workingModel.config.schedule as AsapFeedSchedule)
                                                                  .endTime,
                                                              'H:mm',
                                                          )
                                                        : undefined
                                                }
                                                onTimeChange={handleEndTimeChange}
                                                showNow={false}
                                            />
                                            <div className="ant-input-group-addon timezone-addon">
                                                {domain?.timezone}
                                            </div>
                                        </Input.Group>
                                    </Form.Item>
                                )}
                            </Form.Item>
                            <Form.Item label="Send Days">
                                <DayPartSelect
                                    size="small"
                                    daysOnly={true}
                                    dayFormat={'ddd'}
                                    weekBeginsOnSunday={true}
                                    value={Object.entries(dayIsoMap).reduce((acc, v) => {
                                        workingModel.config.schedule.dayOfWeekIso.includes(v[1])
                                            ? (acc[v[0]] = true)
                                            : (acc[v[0]] = false)

                                        return acc
                                    }, {} as any)}
                                    onChange={(days: any) => {
                                        const dayKeys = Object.keys(days)
                                        const dayOfWeekIso = dayKeys
                                            .map((day) => {
                                                return days[day] === true && dayIsoMap[day]
                                            })
                                            .filter((v) => v)

                                        const updated = workingModel.toMutable()
                                        updated.config.schedule.dayOfWeekIso = dayOfWeekIso

                                        setWorkingModel(updated)
                                    }}
                                />
                            </Form.Item>
                            {workingModel.config.schedule.sendStrategy === FeedSendStrategy.FIXED && (
                                <Form.Item
                                    label={
                                        <div className="send-delay-label">
                                            Pre-Schedule
                                            <Tooltip
                                                title="The pre-schedule determines how far in advance the notification
                                                will be scheduled for delivery. For example, if the send time is
                                                set for 2:00pm and a 15 minute pre-schedule is, set the notification
                                                will be created at 1:45pm and scheduled to deliver at 2:00pm."
                                            >
                                                <InfoCircleOutlined />
                                            </Tooltip>
                                        </div>
                                    }
                                    className="send-delay"
                                >
                                    <Switch
                                        className="send-delay-switch"
                                        size="small"
                                        checked={!!(workingModel.config.schedule as FixedFeedSchedule).deliveryDelay}
                                        onChange={(v) => {
                                            const updated = workingModel.clone()
                                            const updatedDeliveryDelay = v
                                                ? relativeDateOf({
                                                      intervalSeconds: 3600,
                                                      displayMetric: RelativeDateDisplayMetric.MINUTES,
                                                  })
                                                : undefined

                                            ;(updated.config.schedule as MutableFixedFeedSchedule).deliveryDelay =
                                                updatedDeliveryDelay

                                            setWorkingModel(updated)
                                            setPreScheduleInputValue(
                                                calculatePreScheduleInputValue(updatedDeliveryDelay),
                                            )
                                        }}
                                    />
                                    <Input
                                        className="send-input"
                                        size="small"
                                        disabled={!(workingModel.config.schedule as FixedFeedSchedule).deliveryDelay}
                                        maxLength={4}
                                        value={preScheduleInputValue}
                                        onChange={(ev) => {
                                            const raw = ev.target.value.trim()
                                            const frequencyValue = !raw || isNaN(raw as any) ? 0 : parseInt(raw, 10)
                                            const displayMetric =
                                                (workingModel.config.schedule as FixedFeedSchedule).deliveryDelay
                                                    ?.displayMetric ?? RelativeDateDisplayMetric.MINUTES
                                            const intervalSeconds = calculateIntervalSecondsInputValue(
                                                frequencyValue,
                                                displayMetric,
                                            )

                                            const updated = workingModel.toMutable()

                                            ;(updated.config.schedule as MutableFixedFeedSchedule).deliveryDelay =
                                                relativeDateOf({
                                                    intervalSeconds,
                                                    displayMetric,
                                                })

                                            setWorkingModel(updated)
                                            setPreScheduleInputValue(frequencyValue)
                                        }}
                                        addonAfter={
                                            <Select<RelativeDateDisplayMetric>
                                                size="small"
                                                value={
                                                    (workingModel.config.schedule as FixedFeedSchedule).deliveryDelay
                                                        ?.displayMetric ?? RelativeDateDisplayMetric.MINUTES
                                                }
                                                disabled={
                                                    !(workingModel.config.schedule as FixedFeedSchedule).deliveryDelay
                                                }
                                                onChange={(metric) => {
                                                    const updated = workingModel.toMutable()

                                                    ;(
                                                        updated.config.schedule as MutableFixedFeedSchedule
                                                    ).deliveryDelay = relativeDateOf({
                                                        intervalSeconds: calculateIntervalSecondsInputValue(
                                                            preScheduleInputValue,
                                                            metric,
                                                        ),
                                                        displayMetric: metric,
                                                    })

                                                    setWorkingModel(updated)
                                                }}
                                            >
                                                <Select.Option value={RelativeDateDisplayMetric.MINUTES}>
                                                    Minutes
                                                </Select.Option>
                                                <Select.Option value={RelativeDateDisplayMetric.HOURS}>
                                                    Hours
                                                </Select.Option>
                                            </Select>
                                        }
                                    />
                                </Form.Item>
                            )}
                            {workingModel.config.schedule.sendStrategy === FeedSendStrategy.ASAP && (
                                <Form.Item
                                    label="Send Frequency"
                                    tooltip={{
                                        title: 'How often can notification be sent from this feed?',
                                        icon: <QuestionCircleOutlined className="feed-send-frequency-info" />,
                                        className: 'feed-send-frequency-info',
                                    }}
                                >
                                    <Input
                                        className="send-input"
                                        size="small"
                                        value={
                                            (workingModel.config.schedule as AsapFeedSchedule).minFrequency
                                                ?.intervalSeconds
                                                ? secondsToMinutes(
                                                      (workingModel.config.schedule as AsapFeedSchedule).minFrequency
                                                          ?.intervalSeconds,
                                                  )
                                                : undefined
                                        }
                                        onChange={(ev) => {
                                            const updated = workingModel.toMutable()

                                            ;(updated.config.schedule as MutableAsapFeedSchedule).minFrequency =
                                                relativeDateOf({
                                                    intervalSeconds: minutesToSeconds(parseInt(ev.target.value, 10)),
                                                    displayMetric: RelativeDateDisplayMetric.MINUTES,
                                                })

                                            setWorkingModel(updated)
                                        }}
                                        addonAfter={'Minutes'}
                                    />
                                </Form.Item>
                            )}
                            <Form.Item
                                label={
                                    <div className="holiday-filter-label">
                                        Holiday Filter
                                        <Tooltip
                                            overlayClassName="wide-tooltip"
                                            title={
                                                <React.Fragment>
                                                    <ul className="tooltip-ul">
                                                        <li>
                                                            NYSE: NYSE market holidays (used by all other US exchanges,
                                                            including NASDAQ)
                                                        </li>

                                                        <li>
                                                            European Central Bank: Trans-European market holidays (used
                                                            by all Euro-based exchanges)
                                                        </li>
                                                    </ul>
                                                </React.Fragment>
                                            }
                                        >
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </div>
                                }
                                name="holiday-filter"
                            >
                                <Radio.Group
                                    size="small"
                                    buttonStyle="solid"
                                    onChange={(ev) => {
                                        const updated = workingModel.toMutable()
                                        const updatedHolidaySkip = new MutableHolidaySkipConfiguration()
                                        updatedHolidaySkip.enabled = false

                                        if (
                                            [HolidayFilter.country, HolidayFilter.financial].includes(ev.target.value)
                                        ) {
                                            updatedHolidaySkip.holidayFilterType = ev.target.value
                                            updatedHolidaySkip.categoryCode =
                                                ev.target.value === HolidayFilter.country ? 'US' : 'XNYS'
                                            updatedHolidaySkip.enabled = true
                                        }

                                        updated.config.schedule.holidaySkip = updatedHolidaySkip
                                        setWorkingModel(updated)
                                    }}
                                    value={workingModel.config.schedule.holidaySkip?.holidayFilterType ?? 'NONE'}
                                >
                                    <Radio.Button value={'NONE'}>
                                        <span>None</span>
                                    </Radio.Button>
                                    <Radio.Button value={HolidayFilter.country}>
                                        <span>Country</span>
                                    </Radio.Button>
                                    <Radio.Button value={HolidayFilter.financial}>Financial / Bank</Radio.Button>
                                </Radio.Group>
                                {workingModel.config.schedule.holidaySkip?.holidayFilterType ===
                                    HolidayFilter.country && (
                                    <>
                                        <span>holidays for</span>
                                        <Select
                                            className="country-holiday-select-list"
                                            size="small"
                                            value={workingModel.config.schedule.holidaySkip?.categoryCode}
                                            dropdownStyle={{ minWidth: '200px' }}
                                            onChange={(val) => {
                                                const updated = workingModel.toMutable()

                                                updated.config.schedule.holidaySkip.categoryCode = val

                                                setWorkingModel(updated)
                                            }}
                                        >
                                            {holidaySkipCountryOptions.map((o) => (
                                                <Select.Option key={o.name} value={o.value} title={o.name}>
                                                    {o.name}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </>
                                )}
                                {workingModel.config.schedule.holidaySkip?.holidayFilterType ===
                                    HolidayFilter.financial && (
                                    <Radio.Group
                                        size="small"
                                        buttonStyle="solid"
                                        onChange={(ev) => {
                                            const updated = workingModel.toMutable()

                                            updated.config.schedule.holidaySkip.categoryCode = ev.target.value

                                            setWorkingModel(updated)
                                        }}
                                        value={workingModel.config.schedule.holidaySkip?.categoryCode}
                                    >
                                        <Radio.Button value={'XNYS'}>
                                            <span>NYSE</span>
                                        </Radio.Button>
                                        <Radio.Button value={'ECB'}>
                                            <span>European Central Bank</span>
                                        </Radio.Button>
                                    </Radio.Group>
                                )}
                            </Form.Item>
                            <Form.Item
                                label={
                                    <div className="notification-lifespan-label">
                                        Notification Lifespan
                                        <Tooltip
                                            overlayClassName="wide-tooltip"
                                            title={
                                                <React.Fragment>
                                                    <p>
                                                        Notification Lifespan determines how long the notification will
                                                        attempt to be delivered to the subscriber.
                                                    </p>

                                                    <ul className="tooltip-ul">
                                                        <li>
                                                            Domain Default: When selected, this option will always use
                                                            the current default lifespan set in domain settings.
                                                        </li>
                                                        <li>
                                                            Custom: When selected, this option will allow you to choose
                                                            a lifespan to apply to all notifications created via this
                                                            feed.
                                                        </li>
                                                    </ul>
                                                </React.Fragment>
                                            }
                                        >
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </div>
                                }
                                className="notification-lifespan"
                            >
                                <Radio.Group
                                    size="small"
                                    buttonStyle="solid"
                                    value={isDomainDefaultNotificationLifespan}
                                    onChange={(ev) => {
                                        setIsDefaultNotificationLifespan(ev.target.value)
                                        if (ev.target.value) {
                                            const updated = workingModel.toMutable()
                                            updated.config.notificationLifespan = null

                                            const domainDefaultNotificationLifespan = calculateRelativeDate(
                                                domain.default_notification_ttl_seconds,
                                                enumFromValue(
                                                    RelativeDateDisplayMetric,
                                                    domain.display_meta.default_notification_ttl_metric,
                                                ),
                                            )

                                            setWorkingModel(updated)
                                            setNotificationLifespanInputValue(domainDefaultNotificationLifespan)
                                        }
                                    }}
                                >
                                    <Radio.Button value={true}>
                                        <span>Domain Default</span>
                                    </Radio.Button>
                                    <Radio.Button value={false}>
                                        <span>Custom</span>
                                    </Radio.Button>
                                </Radio.Group>
                                <Input
                                    className="notification-lifespan-input"
                                    size="small"
                                    disabled={isDomainDefaultNotificationLifespan}
                                    maxLength={4}
                                    value={notificationLifespanInputValue}
                                    onChange={(ev) => {
                                        const raw = ev.target.value.trim()
                                        const notificationLifespanValue =
                                            !raw || isNaN(raw as any) ? 0 : parseInt(raw, 10)
                                        const displayMetric =
                                            workingModel.config.notificationLifespan?.displayMetric ??
                                            getDefaultNotificationTtlMetric(domain)
                                        const intervalSeconds = calculateIntervalSecondsInputValue(
                                            notificationLifespanValue,
                                            displayMetric,
                                        )

                                        const updated = workingModel.toMutable()

                                        updated.config.notificationLifespan = relativeDateOf({
                                            intervalSeconds,
                                            displayMetric,
                                        })

                                        setWorkingModel(updated)
                                        setNotificationLifespanInputValue(notificationLifespanValue)
                                    }}
                                    addonAfter={
                                        <Select<RelativeDateDisplayMetric>
                                            size="small"
                                            value={
                                                workingModel.config.notificationLifespan?.displayMetric ??
                                                enumFromValue(
                                                    RelativeDateDisplayMetric,
                                                    domain?.display_meta?.default_notification_ttl_metric ??
                                                        RelativeDateDisplayMetric.DAYS,
                                                )
                                            }
                                            disabled={isDomainDefaultNotificationLifespan}
                                            onChange={(metric) => {
                                                const updated = workingModel.toMutable()

                                                updated.config.notificationLifespan = relativeDateOf({
                                                    intervalSeconds: calculateIntervalSecondsInputValue(
                                                        notificationLifespanInputValue,
                                                        metric,
                                                    ),
                                                    displayMetric: metric,
                                                })

                                                setWorkingModel(updated)
                                            }}
                                        >
                                            <Select.Option value={RelativeDateDisplayMetric.DAYS}>Days</Select.Option>
                                            <Select.Option value={RelativeDateDisplayMetric.HOURS}>Hours</Select.Option>
                                        </Select>
                                    }
                                />
                            </Form.Item>
                        </Well>
                        <Well
                            header={
                                <div className="aqe-well-title">
                                    Notification Fields
                                    <Tooltip title="Choose the notification fields that should be populated">
                                        <InfoCircleOutlined />
                                    </Tooltip>
                                </div>
                            }
                            hideFooter={true}
                            className="notification-fields-well type-3"
                        >
                            <Table
                                columns={[
                                    {
                                        title: 'Field',
                                        dataIndex: 'field',
                                        key: 'field',
                                        width: '100px',
                                    },
                                    {
                                        title: 'Enabled',
                                        dataIndex: 'enabled',
                                        key: 'enabled',
                                        render: (_, record) =>
                                            record.enabled && (
                                                <div className="notification-fields-enabled-switch">
                                                    <Switch
                                                        size="small"
                                                        disabled={record.field === 'Title'}
                                                        checked={
                                                            workingModel.config.fieldOptions[record.field.toLowerCase()]
                                                                ?.enabled
                                                        }
                                                        onChange={(v) => {
                                                            const updated = workingModel.toMutable()
                                                            const fieldOptionProperty = record.field.toLowerCase()

                                                            updated.config.fieldOptions[fieldOptionProperty].enabled = v

                                                            if (workingModel.feedType === FeedType.JSON && !v) {
                                                                updated.config.feedJsonpath = new MutableFeedPath()
                                                                updated.config.feedJsonpath.articles =
                                                                    workingModel.config.feedJsonpath.articles
                                                                updated.config.feedJsonpath.properties =
                                                                    workingModel.config.feedJsonpath.properties

                                                                // if this field is disabled, this will remove it from the updated
                                                                // feedJsonpath object so it is not persisted
                                                                // @ts-ignore
                                                                delete updated.config.feedJsonpath.properties[
                                                                    fieldOptionProperty
                                                                ]
                                                            }

                                                            if (workingModel.feedType === FeedType.XML && !v) {
                                                                updated.config.feedXpath = new MutableFeedPath()
                                                                updated.config.feedXpath.articles =
                                                                    workingModel.config.feedXpath.articles
                                                                updated.config.feedXpath.properties =
                                                                    workingModel.config.feedXpath.properties

                                                                // if this field is disabled, this will remove it from the updated
                                                                // feedXpath object so it is not persisted
                                                                // @ts-ignore
                                                                delete updated.config.feedXpath.properties[
                                                                    fieldOptionProperty
                                                                ]
                                                            }

                                                            setWorkingModel(updated)
                                                        }}
                                                    />

                                                    {record.field === 'Title' && (
                                                        <Tooltip title="The article's title is a required field and cannot be disabled.">
                                                            <InfoCircleOutlined />
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            ),
                                        width: '75px',
                                    },
                                    {
                                        title: (
                                            <div>
                                                Page Analysis
                                                <Tooltip title="If enabled, we will attempt to fetch and override the feed’s notification content from the landing URL’s metadata.">
                                                    <InfoCircleOutlined />
                                                </Tooltip>
                                            </div>
                                        ),
                                        dataIndex: 'fieldAnalysis',
                                        key: 'fieldAnalysis',
                                        render: (_, record) =>
                                            record.pageAnalysis && (
                                                <Switch
                                                    size="small"
                                                    disabled={
                                                        !workingModel.config.fieldOptions[record.field.toLowerCase()]
                                                            ?.enabled
                                                    }
                                                    checked={
                                                        workingModel.config.fieldOptions[record.field.toLowerCase()]
                                                            ?.use_page_analysis
                                                    }
                                                    onChange={(v) => {
                                                        const updated = workingModel.toMutable()

                                                        updated.config.fieldOptions[
                                                            record.field.toLowerCase()
                                                        ].use_page_analysis = v

                                                        setWorkingModel(updated)
                                                    }}
                                                />
                                            ),
                                        width: '110px',
                                    },
                                    {
                                        title: 'Text Length Limit',
                                        dataIndex: 'textLengthLimit',
                                        key: 'textLengthLimit',
                                        render: (_, record) =>
                                            record.textLengthLimit && (
                                                <div>
                                                    <Switch
                                                        size="small"
                                                        disabled={
                                                            !workingModel.config.fieldOptions[
                                                                record.field.toLowerCase()
                                                            ]?.enabled
                                                        }
                                                        checked={
                                                            workingModel.config.fieldOptions[record.field.toLowerCase()]
                                                                ?.character_limit
                                                        }
                                                        onChange={(v) => {
                                                            const updated = workingModel.toMutable()

                                                            updated.config.fieldOptions[
                                                                record.field.toLowerCase()
                                                            ].character_limit = v === true ? 100 : null
                                                            updated.config.fieldOptions[
                                                                record.field.toLowerCase()
                                                            ].placeholder = v === true ? '...' : null

                                                            setWorkingModel(updated)
                                                        }}
                                                    />
                                                    <Input
                                                        className="text-length-limit-input"
                                                        size="small"
                                                        disabled={
                                                            !workingModel.config.fieldOptions[
                                                                record.field.toLowerCase()
                                                            ]?.enabled ||
                                                            !workingModel.config.fieldOptions[
                                                                record.field.toLowerCase()
                                                            ]?.character_limit ||
                                                            !workingModel.config.fieldOptions[
                                                                record.field.toLowerCase()
                                                            ]?.enabled
                                                        }
                                                        value={
                                                            workingModel.config.fieldOptions[record.field.toLowerCase()]
                                                                ?.character_limit
                                                        }
                                                        maxLength={3}
                                                        onChange={(ev) => {
                                                            const updated = workingModel.toMutable()

                                                            updated.config.fieldOptions[
                                                                record.field.toLowerCase()
                                                            ].character_limit = parseInt(ev.target.value, 10)

                                                            setWorkingModel(updated)
                                                        }}
                                                    />
                                                </div>
                                            ),
                                        width: '135px',
                                    },
                                    {
                                        title: 'Limit Exceeded Placeholder',
                                        dataIndex: 'limitExceededPlaceholder',
                                        key: 'limitExceededPlaceholder',
                                        render: (_, record) =>
                                            record.limitExceededPlaceHolder && (
                                                <div>
                                                    <Switch
                                                        size="small"
                                                        disabled={
                                                            !workingModel.config.fieldOptions[
                                                                record.field.toLowerCase()
                                                            ]?.enabled
                                                        }
                                                        checked={
                                                            workingModel.config.fieldOptions[record.field.toLowerCase()]
                                                                ?.placeholder
                                                        }
                                                        onChange={(v) => {
                                                            const updated = workingModel.toMutable()

                                                            updated.config.fieldOptions[
                                                                record.field.toLowerCase()
                                                            ].placeholder = v === true ? '...' : null

                                                            setWorkingModel(updated)
                                                        }}
                                                    />
                                                    <Input
                                                        className="limit-exceeded-placeholder-input"
                                                        size="small"
                                                        value={
                                                            workingModel.config.fieldOptions[record.field.toLowerCase()]
                                                                ?.placeholder
                                                        }
                                                        disabled={
                                                            !workingModel.config.fieldOptions[
                                                                record.field.toLowerCase()
                                                            ]?.enabled ||
                                                            !workingModel.config.fieldOptions[
                                                                record.field.toLowerCase()
                                                            ]?.placeholder
                                                        }
                                                        onChange={(ev) => {
                                                            const updated = workingModel.clone()

                                                            updated.config.fieldOptions[
                                                                record.field.toLowerCase()
                                                            ].placeholder = ev.target.value

                                                            setWorkingModel(updated)
                                                        }}
                                                    />
                                                </div>
                                            ),
                                        width: '200px',
                                    },
                                ]}
                                dataSource={[
                                    {
                                        key: '1',
                                        field: 'Title',
                                        enabled: true,
                                        pageAnalysis: true,
                                        textLengthLimit: true,
                                        limitExceededPlaceHolder: true,
                                    },
                                    {
                                        key: '2',
                                        field: 'Body',
                                        enabled: true,
                                        pageAnalysis: true,
                                        textLengthLimit: true,
                                        limitExceededPlaceHolder: true,
                                    },
                                    {
                                        key: '3',
                                        field: 'Image',
                                        enabled: true,
                                        pageAnalysis: true,
                                        textLengthLimit: false,
                                        limitExceededPlaceHolder: false,
                                    },
                                    {
                                        key: '4',
                                        field: 'Keywords',
                                        enabled: true,
                                        pageAnalysis: true,
                                        textLengthLimit: false,
                                        limitExceededPlaceHolder: false,
                                    },
                                ]}
                                pagination={false}
                                size="small"
                            />
                        </Well>
                        {workingModel.feedType === FeedType.JSON && (
                            <FeedPathPropertyExpressionsWell
                                workingModel={workingModel}
                                setWorkingModel={setWorkingModel}
                                onChange={(next) => {
                                    const update = workingModel.toMutable()
                                    update.config.feedJsonpath = next
                                    if (next.properties) {
                                        update.config.feedJsonpath.properties = next.properties
                                    }

                                    setWorkingModel(update)
                                }}
                                type={FeedType.JSON}
                            />
                        )}
                        {workingModel.feedType === FeedType.XML && (
                            <FeedPathPropertyExpressionsWell
                                workingModel={workingModel}
                                setWorkingModel={setWorkingModel}
                                onChange={(next) => {
                                    const update = workingModel.toMutable()
                                    update.config.feedXpath = next
                                    if (next.properties) {
                                        update.config.feedXpath.properties = next.properties
                                    }

                                    setWorkingModel(update)
                                }}
                                type={FeedType.XML}
                            />
                        )}
                        <Well
                            title="Article Constraints"
                            disableSubmit={mode === FdfFormMode.EDIT && Object.keys(getCurrentValues()).length === 0}
                            onSubmit={async () => form.submit()}
                            onCancel={() => {
                                !props.history?.location?.state
                                    ? appService.route(`/domains/${domainId}/feeds`)
                                    : appService.routeBack()
                            }}
                            className="type-3"
                        >
                            <FeedArticleAgeEditor
                                articleMaxAge={workingModel.config.articleMaxAge}
                                articleAgeInputValue={articleAgeInputValue}
                                onChange={(next, frequencyValue) => {
                                    if (frequencyValue) {
                                        setArticleAgeInputValue(frequencyValue)
                                    }

                                    const update = workingModel.toMutable()
                                    update.config.articleMaxAge = next
                                    setWorkingModel(update)
                                }}
                            ></FeedArticleAgeEditor>
                            <TemplateKeywordsField
                                label={
                                    <div className="article-constraints-input-label">
                                        Required Keywords
                                        <Tooltip
                                            title="The article must contain at least one of the specified keywords to be eligible for this feed.
                                            Keywords are not case sensitive but must be exact matches. This field does not support regex pattern matching."
                                        >
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </div>
                                }
                                size="small"
                                disableDropdown={true}
                                value={workingModel.config.requiredKeywords ?? undefined}
                                onChange={(v) => {
                                    const updated = workingModel.toMutable()
                                    updated.config.requiredKeywords = v

                                    setWorkingModel(updated)
                                }}
                            />
                            <TemplateKeywordsField
                                label={
                                    <div className="article-constraints-input-label">
                                        Restricted Keywords
                                        <Tooltip
                                            title="If the article contains at least one of the specified keywords it will not be eligible for this feed.
                                            Keywords are not case sensitive but must be exact matches. This field does not support regex pattern matching."
                                        >
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </div>
                                }
                                size="small"
                                disableDropdown={true}
                                value={workingModel.config.restrictedKeywords ?? undefined}
                                onChange={(v) => {
                                    const updated = workingModel.toMutable()
                                    updated.config.restrictedKeywords = v

                                    setWorkingModel(updated)
                                }}
                            />
                            <TemplateKeywordsField
                                label={
                                    <div className="article-constraints-input-label">
                                        URL Allow List
                                        <Tooltip
                                            title="The article’s URL must contain (wildcard) at least one of the specified URL patterns to be eligible for this feed.
                                            This field supports regular expression syntax for more complicated pattern matching."
                                        >
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </div>
                                }
                                size="small"
                                disableDropdown={true}
                                value={workingModel.config.urlAllowlist ?? undefined}
                                onChange={(v) => {
                                    const updated = workingModel.toMutable()
                                    updated.config.urlAllowlist = v

                                    setWorkingModel(updated)
                                }}
                            />
                            <TemplateKeywordsField
                                label={
                                    <div className="article-constraints-input-label">
                                        URL Deny List
                                        <Tooltip
                                            title="The article’s URL cannot contain (wildcard) any of the specified URL patterns to be eligible for this feed.
                                            This field supports regular expression syntax for more complicated pattern matching."
                                        >
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </div>
                                }
                                size="small"
                                disableDropdown={true}
                                value={workingModel.config.urlDenylist ?? undefined}
                                onChange={(v) => {
                                    const updated = workingModel.toMutable()
                                    updated.config.urlDenylist = v

                                    setWorkingModel(updated)
                                }}
                            />
                            <TemplateKeywordsField
                                label={
                                    <div className="article-constraints-input-label">
                                        Title Deny List
                                        <Tooltip
                                            title="The article’s Title cannot contain (wildcard) any of the specified patterns to be eligible for this feed.
                                            This field supports regular expression syntax for more complicated pattern matching."
                                        >
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </div>
                                }
                                size="small"
                                disableDropdown={true}
                                value={workingModel.config.titleDenylist ?? undefined}
                                onChange={(v) => {
                                    const updated = workingModel.toMutable()
                                    updated.config.titleDenylist = v

                                    setWorkingModel(updated)
                                }}
                            />
                            <Form.Item
                                className="article-recycling-switch"
                                label={
                                    <div className="article-constraints-input-label">
                                        Allow Article Recycling
                                        <Tooltip
                                            title="When enabled the feed will re-send previously sent articles when no unsent articles are available.
                                            The articles will be re-sent in order of the oldest sent article to the most recently sent."
                                        >
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </div>
                                }
                            >
                                <Switch
                                    size="small"
                                    checked={workingModel.config.articleRecycling ?? false}
                                    onChange={(v) => {
                                        const updated = workingModel.toMutable()
                                        updated.config.articleRecycling = v

                                        setWorkingModel(updated)
                                    }}
                                />
                            </Form.Item>
                        </Well>
                    </div>

                    <div className="feed-data-form-right-pane">
                        <ReachWell
                            className={classnames('feed-reach-estimate type-3')}
                            actions={
                                loadingReach ? (
                                    <Loading3QuartersOutlined spin={true} />
                                ) : !hasReachEstimate ? (
                                    '--'
                                ) : (
                                    `~ ${numberWithCommas(currReach!)}`
                                )
                            }
                            children={undefined}
                        />

                        <Well title="Notification Preview" hideFooter={true} className="type-3">
                            {isValidPreviewRequest() && !loadingPreview && previewNotifications.length === 0 ? (
                                <div>No articles matching configuration found.</div>
                            ) : (
                                <NotificationSummaryCard
                                    className="mode-ghost"
                                    loading={loadingPreview}
                                    channels={workingModel.config.channels}
                                    domain={{
                                        ...domain,
                                        displayName: domain.display_name,
                                        defaultIconUrl: domain.default_icon_url,
                                    }}
                                    notification={
                                        previewNotifications.length > 0 &&
                                        extractSummaryNotification(previewNotifications[previewIndex])
                                    }
                                    hideStats={true}
                                    hideFooter={true}
                                    previewState={PreviewState.EXPANDED}
                                />
                            )}
                        </Well>

                        <div className="next-notification">
                            <Button
                                size="small"
                                className="next-notification-button"
                                disabled={previewNotifications.length < 2}
                                onClick={() => {
                                    if (previewIndex === previewNotifications.length - 1) {
                                        setPreviewIndex(0)
                                    } else {
                                        setPreviewIndex(previewIndex + 1)
                                    }
                                }}
                                loading={false}
                            >
                                Next Notification
                            </Button>
                        </div>

                        <Well
                            title="Next 7 Occurrences"
                            hideFooter={true}
                            loading={loadingPreview}
                            className="next-seven-occurrences-well type-3"
                        >
                            {!isValidPreviewRequest() &&
                                'Please complete all required fields to view next occurrences.'}
                            {isValidPreviewRequest() &&
                                previewNotifications.length === 0 &&
                                'No articles matching configuration found.'}
                            {isValidPreviewRequest() &&
                                previewNotifications.length > 0 &&
                                estimatedPreviewDeliveries.length > 0 &&
                                estimatedPreviewDeliveries.map((d, idx) => {
                                    return (
                                        <div key={idx}>
                                            {workingModel.config.schedule.sendStrategy !==
                                            FeedSendStrategy.SUBSCRIBER_TIME_ZONE
                                                ? moment(d)
                                                      .tz(domain?.timezone)
                                                      .format('dddd, MMMM D, YYYY h:mm A')
                                                : moment.parseZone(d).format('dddd, MMMM D, YYYY h:mm A') + ' STZ'}
                                        </div>
                                    )
                                })}
                        </Well>
                    </div>
                </div>
            </Form>
        </div>
    )
}

export default FeedDataForm
